import { Box, Card, CardBody, Divider, Flex, HStack, SimpleGrid, Spinner, Stack, Text, Tooltip, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { ReactNode } from "react";
import { adminGetAnalysisSearching } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import { colors } from "../../../styles";
import { IAdminAnalysisSearching } from "../../../types";

type CountByUserType = IAdminAnalysisSearching["count"]["searchCount"];
type UserTypeKey = keyof CountByUserType;

const USER_TYPES: { key: UserTypeKey; label: string; color: string }[] = [
    { key: "owner", label: "개국약사", color: colors.blue },
    { key: "pharmacist", label: "약사", color: colors.covalt },
    { key: "student", label: "약대생", color: colors.teal },
    { key: "member", label: "멤버", color: colors.link },
    { key: "guest", label: "비회원", color: colors.brown },
];

const ADDITIONAL_ITEMS: { key: keyof IAdminAnalysisSearching["additional"]; label: string }[] = [
    { key: "search_count", label: "검색 수" },
    { key: "similar_modal", label: "동일성분" },
    { key: "ingredient_group", label: "같은 성분 묶어보기" },
    { key: "keyword_click", label: "키워드" },
    { key: "online_mall_click", label: "온라인몰" },
];

function safeNumber(value: number | undefined) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function totalOf(counts: CountByUserType | undefined) {
    return USER_TYPES.reduce((sum, type) => sum + safeNumber(counts?.[type.key]), 0);
}

function formatCount(value: number) {
    return Math.round(value).toLocaleString("ko-KR");
}

function formatMeasure(value: number) {
    return value.toLocaleString("ko-KR", { maximumFractionDigits: 1 });
}

function formatShare(part: number, total: number) {
    if (total <= 0 || part <= 0) return "0%";
    const percent = (part / total) * 100;
    if (percent < 0.1) return "<0.1%";
    return `${(Math.round(percent * 10) / 10).toLocaleString("ko-KR", { maximumFractionDigits: 1 })}%`;
}

function ratioWidth(value: number, max: number) {
    if (max <= 0 || value <= 0) return "0%";
    return `${(value / max) * 100}%`;
}

function rowsFromCounts(counts: CountByUserType | undefined) {
    return USER_TYPES.map((type) => ({
        ...type,
        value: safeNumber(counts?.[type.key]),
    }));
}

function isAnalysisSearching(data: IAdminAnalysisSearching | undefined): data is IAdminAnalysisSearching {
    return Boolean(
        data?.count?.searchCount
        && data.count.searchUserCount
        && data.count.averageSearchCount
        && data.active?.once
        && data.active.much
        && data.active.click
        && data.cycle
        && data.additional
    );
}

function AnalysisCard({ children }: { children: ReactNode }) {
    return (
        <Card
            width={"100%"}
            height={"100%"}
            border={`0.5px solid ${colors.g4}`}
            borderRadius={10}
            shadow={"md"}
            backgroundColor={colors.white}
        >
            <CardBody>{children}</CardBody>
        </Card>
    );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
    return (
        <Stack spacing={1}>
            <Text fontSize={18} fontWeight={700} color={colors.black}>{title}</Text>
            <Text fontSize={13} fontWeight={400} color={colors.g2}>{description}</Text>
        </Stack>
    );
}

function BreakdownRows({
    rows,
    max,
    formatValue,
}: {
    rows: { key: string; label: string; color: string; value: number }[];
    max: number;
    formatValue: (value: number) => ReactNode;
}) {
    return (
        <VStack align={"stretch"} spacing={0.5}>
            {rows.map((row) => (
                <HStack
                    key={row.key}
                    spacing={3}
                    px={2}
                    py={1.5}
                    mx={-2}
                    borderRadius={"md"}
                    _hover={{ backgroundColor: colors.g5 }}
                >
                    <HStack width={"84px"} flexShrink={0} spacing={2}>
                        <Box boxSize={"8px"} borderRadius={"full"} backgroundColor={row.color} flexShrink={0} />
                        <Text fontSize={13} fontWeight={500} color={colors.g1} noOfLines={1}>{row.label}</Text>
                    </HStack>
                    <Box flex={1} height={"8px"} backgroundColor={colors.g5} borderRadius={"full"} overflow={"hidden"}>
                        <Box
                            height={"100%"}
                            width={ratioWidth(row.value, max)}
                            backgroundColor={row.color}
                            borderRadius={"full"}
                        />
                    </Box>
                    <Box
                        flexShrink={0}
                        minW={"148px"}
                        textAlign={"right"}
                        fontSize={13}
                        fontWeight={700}
                        color={colors.black}
                        whiteSpace={"nowrap"}
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                        {formatValue(row.value)}
                    </Box>
                </HStack>
            ))}
        </VStack>
    );
}

function ShareCard({ title, counts }: { title: string; counts: CountByUserType | undefined }) {
    const rows = rowsFromCounts(counts);
    const total = rows.reduce((sum, row) => sum + row.value, 0);

    return (
        <AnalysisCard>
            <VStack align={"stretch"} spacing={4}>
                <Stack spacing={2}>
                    <Text fontSize={16} fontWeight={500} color={colors.black} minH={"48px"} lineHeight={"24px"}>{title}</Text>
                    <HStack align={"baseline"} spacing={2}>
                        <Text fontSize={13} fontWeight={500} color={colors.g2}>전체</Text>
                        <Text fontSize={36} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {formatCount(total)}
                        </Text>
                    </HStack>
                </Stack>

                <Flex
                    height={"10px"}
                    width={"100%"}
                    backgroundColor={colors.g5}
                    borderRadius={"full"}
                    overflow={"hidden"}
                    role={"img"}
                    aria-label={rows.map((row) => `${row.label} ${formatShare(row.value, total)} ${formatCount(row.value)}`).join(", ")}
                >
                    {rows.map((row) => row.value > 0 ? (
                        <Tooltip
                            key={row.key}
                            label={`${row.label} ${formatShare(row.value, total)} (${formatCount(row.value)})`}
                            placement={"top"}
                            hasArrow
                            openDelay={200}
                        >
                            <Box flex={row.value} minW={0} height={"100%"} backgroundColor={row.color} />
                        </Tooltip>
                    ) : null)}
                </Flex>

                <BreakdownRows
                    rows={rows}
                    max={total}
                    formatValue={(value) => (
                        <>
                            {formatShare(value, total)}
                            <Text as={"span"} fontWeight={400} color={colors.g2}> ({formatCount(value)})</Text>
                        </>
                    )}
                />
            </VStack>
        </AnalysisCard>
    );
}

function AverageCard({
    searchCount,
    searchUserCount,
    averageSearchCount,
}: {
    searchCount: CountByUserType | undefined;
    searchUserCount: CountByUserType | undefined;
    averageSearchCount: CountByUserType | undefined;
}) {
    const rows = rowsFromCounts(averageSearchCount);
    const max = Math.max(...rows.map((row) => row.value), 0);
    const users = totalOf(searchUserCount);
    const overall = users > 0 ? totalOf(searchCount) / users : null;

    return (
        <AnalysisCard>
            <VStack align={"stretch"} spacing={4}>
                <Stack spacing={2}>
                    <Text fontSize={16} fontWeight={500} color={colors.black} minH={"48px"} lineHeight={"24px"}>1인당 평균 검색</Text>
                    <HStack align={"baseline"} spacing={2}>
                        <Text fontSize={13} fontWeight={500} color={colors.g2}>전체</Text>
                        <Text fontSize={36} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {overall == null ? "—" : formatMeasure(overall)}
                        </Text>
                        <Text fontSize={14} fontWeight={400} color={colors.g2}>회</Text>
                    </HStack>
                    <Text fontSize={12} fontWeight={400} color={colors.g2}>
                        전체는 총 검색 수 ÷ 검색 유저 수입니다. 막대는 유형 중 최댓값 기준입니다.
                    </Text>
                </Stack>
                <BreakdownRows
                    rows={rows}
                    max={max}
                    formatValue={(value) => (
                        <>
                            {formatMeasure(value)}
                            <Text as={"span"} fontWeight={400} color={colors.g2}>회</Text>
                        </>
                    )}
                />
            </VStack>
        </AnalysisCard>
    );
}

function CycleCard({ counts }: { counts: CountByUserType | undefined }) {
    const rows = rowsFromCounts(counts);

    return (
        <AnalysisCard>
            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} gap={6}>
                {rows.map((row) => (
                    <VStack key={row.key} align={"flex-start"} spacing={2}>
                        <HStack spacing={2}>
                            <Box boxSize={"8px"} borderRadius={"full"} backgroundColor={row.color} />
                            <Text fontSize={13} fontWeight={500} color={colors.g2}>{row.label}</Text>
                        </HStack>
                        <HStack align={"baseline"} spacing={1}>
                            <Text fontSize={32} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {formatMeasure(row.value)}
                            </Text>
                            <Text fontSize={14} fontWeight={400} color={colors.g2}>일</Text>
                        </HStack>
                    </VStack>
                ))}
            </SimpleGrid>
        </AnalysisCard>
    );
}

function AdditionalCard({ additional }: { additional: IAdminAnalysisSearching["additional"] | undefined }) {
    const rows = ADDITIONAL_ITEMS.map((item) => ({
        ...item,
        value: safeNumber(additional?.[item.key]),
    }));
    const max = Math.max(...rows.map((row) => row.value), 0);

    return (
        <AnalysisCard>
            <VStack align={"stretch"} spacing={3}>
                {rows.map((row) => (
                    <HStack
                        key={row.key}
                        spacing={4}
                        px={2}
                        py={1.5}
                        mx={-2}
                        borderRadius={"md"}
                        _hover={{ backgroundColor: colors.g5 }}
                    >
                        <Text width={"148px"} flexShrink={0} fontSize={14} fontWeight={500} color={colors.g1}>{row.label}</Text>
                        <Box flex={1} height={"10px"} backgroundColor={colors.g5} borderRadius={"full"} overflow={"hidden"}>
                            <Box
                                height={"100%"}
                                width={ratioWidth(row.value, max)}
                                backgroundColor={colors.blue}
                                borderRadius={"full"}
                            />
                        </Box>
                        <Text
                            width={"88px"}
                            flexShrink={0}
                            textAlign={"right"}
                            fontSize={14}
                            fontWeight={600}
                            color={colors.black}
                            whiteSpace={"nowrap"}
                            sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                            {formatCount(row.value)}
                        </Text>
                    </HStack>
                ))}
            </VStack>
        </AnalysisCard>
    );
}

export default function Searching() {
    const { data, isLoading, isError } = useQuery<IAdminAnalysisSearching>({
        queryKey: ["adminGetAnalysisSearching"],
        queryFn: adminGetAnalysisSearching,
    });

    const analysis = isAnalysisSearching(data) ? data : undefined;

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10} spacing={8}>
                <HStack justifyContent={"space-between"} alignItems={"center"}>
                    <Text fontSize={28} fontWeight={700} color={colors.black}>검색 분석</Text>
                </HStack>

                {isLoading ? (
                    <Spinner color={colors.blue} />
                ) : isError || !analysis ? (
                    <Text fontSize={14} fontWeight={400} color={colors.g2}>검색 분석 데이터를 불러오지 못했습니다.</Text>
                ) : (
                    <>
                        <Stack spacing={4}>
                            <SectionHeading
                                title={"검색 규모"}
                                description={"검색 수와 검색 유저 수는 전체 대비 비율입니다. 1인당 평균은 유형별 수치로 비교합니다."}
                            />
                            <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} gap={5}>
                                <ShareCard title={"검색 수"} counts={analysis.count.searchCount} />
                                <ShareCard title={"검색 유저 수"} counts={analysis.count.searchUserCount} />
                                <Box height={"100%"} gridColumn={{ base: "auto", lg: "1 / -1", xl: "auto" }}>
                                    <AverageCard
                                        searchCount={analysis.count.searchCount}
                                        searchUserCount={analysis.count.searchUserCount}
                                        averageSearchCount={analysis.count.averageSearchCount}
                                    />
                                </Box>
                            </SimpleGrid>
                        </Stack>

                        <Divider />

                        <Stack spacing={4}>
                            <SectionHeading
                                title={"활성 사용자"}
                                description={"최근 2주 내 조건에 해당하는 사용자 수입니다. 막대는 그 조건 안에서의 유형 비율입니다."}
                            />
                            <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} gap={5}>
                                <ShareCard title={"검색 1회 이상 사용자"} counts={analysis.active.once} />
                                <ShareCard title={"하루 2회 이상 검색 사용자"} counts={analysis.active.much} />
                                <Box height={"100%"} gridColumn={{ base: "auto", lg: "1 / -1", xl: "auto" }}>
                                    <ShareCard title={"하루 2회 이상 특수 기능 사용자"} counts={analysis.active.click} />
                                </Box>
                            </SimpleGrid>
                        </Stack>

                        <Divider />

                        <Stack spacing={4}>
                            <SectionHeading
                                title={"검색 주기"}
                                description={"검색과 다음 검색 사이의 평균 간격입니다. 짧을수록 더 자주 검색합니다."}
                            />
                            <CycleCard counts={analysis.cycle} />
                        </Stack>

                        <Divider />

                        <Stack spacing={4}>
                            <SectionHeading
                                title={"특수 기능"}
                                description={"검색 수와 특수 기능 사용 횟수입니다. 막대는 다섯 항목 중 최댓값 기준입니다."}
                            />
                            <AdditionalCard additional={analysis.additional} />
                        </Stack>
                    </>
                )}
            </Stack>
        </AdminOnly>
    );
}
